import 'dayjs/locale/es'
import { DualAxes } from '@ant-design/plots'
import { Spin } from 'antd'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useDashboardFilters } from '@contexts'
import { useMonthlyCompliance } from '@hooks'

import { ChartTitle } from './styled'

dayjs.locale('es')

interface GlobalComplianceChartDatum {
  time: string
  real: number
  plan: number
  compliance: number
}

const SERIES_COLOR_DOMAIN = ['real', 'plan', 'compliance'] as const
const SERIES_COLOR_RANGE = ['#6B7280', '#F59E0B', '#EAEAEA'] as const
const REAL_SERIES_COLOR = SERIES_COLOR_RANGE[0]
const PLAN_SERIES_COLOR = SERIES_COLOR_RANGE[1]
const COMPLIANCE_SERIES_COLOR = SERIES_COLOR_RANGE[2]

function legendSeriesKey(datum: unknown): string {
  if (typeof datum === 'string') return datum
  if (datum && typeof datum === 'object') {
    const o = datum as Record<string, unknown>
    if (typeof o.value === 'string') return o.value
    if (typeof o.label === 'string') return o.label
    if (typeof o.id === 'string') return o.id
  }
  return ''
}

function formatCompliancePercent(value: unknown): string {
  let n: number | undefined
  if (typeof value === 'number') {
    n = value
  } else if (typeof value === 'string' && value !== '' && !Number.isNaN(Number(value))) {
    n = Number(value)
  } else if (value && typeof value === 'object') {
    const o = value as Record<string, unknown>
    const raw = o.compliance ?? o.value ?? o.y
    if (typeof raw === 'number') n = raw
  }
  if (n === undefined || n === 0) return ''
  return `${n}%`
}

function formatMonthYearLabel(d: dayjs.Dayjs) {
  const raw = d.format('MMMM YYYY')
  return raw.charAt(0).toLocaleUpperCase('es') + raw.slice(1)
}

export const GlobalComplianceChart = () => {
  const { t } = useTranslation()
  const { selectedMonth } = useDashboardFilters()
  const { data, isLoading } = useMonthlyCompliance()

  const realLabel = t('admin_router.dashboard.global_compliance_legend_real')
  const planLabel = t('admin_router.dashboard.global_compliance_legend_plan')
  const complianceLabel = t('admin_router.dashboard.global_compliance_legend_compliance')

  const chartData: GlobalComplianceChartDatum[] = useMemo(() => {
    if (!data?.days?.length) return []
    return data.days.map(d => ({
      time: (() => {
        const s = dayjs(d.dayDate).format('D-MMM')
        return s.replace(/-([a-z])/, (_, c) => `-${c.toUpperCase()}`)
      })(),
      real: d.real,
      plan: d.plan,
      compliance: d.compliance,
    }))
  }, [data])

  const realPlanMax = useMemo(() => {
    const peak = chartData.reduce((max, d) => Math.max(max, d.real, d.plan), 0)
    // Extra headroom so smooth (Gaussian) line peaks are not clipped at the top.
    return Math.max(1, Math.ceil(peak * 1.25))
  }, [chartData])

  const config = useMemo(
    () => ({
      height: 360,
      clip: false,
      padding: [72, 70, 88, 70],
      legend: {
        color: {
          position: 'bottom' as const,
          layout: { justifyContent: 'center' as const },
          itemMarker: 'round' as const,
          itemMarkerSize: 14,
          itemLabelText: (datum: unknown) => {
            const key = legendSeriesKey(datum)
            if (key === 'real') {
              return realLabel
            }
            if (key === 'plan') {
              return planLabel
            }
            if (key === 'compliance') {
              return complianceLabel
            }
            return key
          },
        },
      },
      interaction: {
        legendFilter: false,
        legendHighlight: false,
        elementHighlight: false,
        elementSelect: false,
        brushFilter: false,
        brushHighlight: false,
      },
      slider: { x: false },
      tooltip: {
        title: 'time',
        items: [
          {
            field: 'real',
            name: realLabel,
            color: REAL_SERIES_COLOR,
          },
          {
            field: 'plan',
            name: planLabel,
            color: PLAN_SERIES_COLOR,
          },
          {
            field: 'compliance',
            name: complianceLabel,
            color: COMPLIANCE_SERIES_COLOR,
            valueFormatter: (compliance: number) => compliance + '%',
          },
        ],
      },
      scale: {
        color: {
          domain: [...SERIES_COLOR_DOMAIN],
          range: [...SERIES_COLOR_RANGE],
        },
      },
      children: [
        {
          type: 'interval' as const,
          data: chartData.map(d => ({
            ...d,
            compliance: Math.min(d.compliance, 100),
          })),
          xField: 'time',
          yField: 'compliance',
          style: {
            fill: COMPLIANCE_SERIES_COLOR,
          },
          interaction: {
            elementHighlight: false,
            elementSelect: false,
          },
          scale: {
            y: {
              key: 'complianceY',
              independent: true,
              nice: false,
              domainMin: 0,
              domainMax: 100,
            },
          },
          axis: { y: { position: 'right' as const } },
          label: {
            text: 'compliance',
            position: 'top',
            dy: -10,
            transform: [],
            formatter: formatCompliancePercent,
            style: { fill: '#666666', fontSize: 11 },
          },
        },
        {
          type: 'line' as const,
          data: chartData,
          xField: 'time',
          yField: 'real',
          shape: 'smooth' as const,
          style: {
            lineWidth: 2,
            stroke: REAL_SERIES_COLOR,
          },
          interaction: {
            elementHighlight: false,
            elementSelect: false,
          },
          scale: {
            y: { key: 'realPlanY', independent: false, domainMin: 0, domainMax: realPlanMax },
          },
        },
        {
          type: 'line' as const,
          data: chartData,
          xField: 'time',
          yField: 'plan',
          shape: 'smooth' as const,
          style: {
            lineWidth: 2,
            stroke: PLAN_SERIES_COLOR,
          },
          interaction: {
            elementHighlight: false,
            elementSelect: false,
          },
          scale: {
            y: { key: 'realPlanY', independent: false, domainMin: 0, domainMax: realPlanMax },
          },
        },
      ],
    }),
    [chartData, realLabel, planLabel, complianceLabel, realPlanMax],
  )

  return (
    <>
      <ChartTitle level={5}>
        {`${t('admin_router.dashboard.global_compliance_chart_title')} - ${formatMonthYearLabel(
          dayjs(selectedMonth),
        )}`}
      </ChartTitle>
      <Spin spinning={isLoading}>
        <DualAxes {...config} />
      </Spin>
    </>
  )
}
